import ReviewBoard from "./ReviewBoard";
import Quote from "./Quote";
import * as styledElements from "./ReviewBoardFlowStyles";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/store";
import BillOfMaterials from "./BillOfMaterials";
import ManufacturersPanel from "../ManufacturersPanel";
import { BoardView } from "../../utils/models/boardToolSteps";
import ProjectDetails from "./ProjectDetails";
import FourOhFour from "../../pages/404";
import { useCustomAlert } from "../../utils/hooks/alerts";
import LoadingIcon from "../common/LoadingIcon";
import {
  BoardIdContext,
  BoardShipmentIds,
  useLoadBoardForReviewBoardFlow,
} from "../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import { useFlashPCBSettings } from "../../utils/hooks/commonly-stored/flashPCBSettings";
import { useCallback, useContext, useEffect } from "react";
import { pageFailureResolved } from "../../reducers/uploadedBoard";
import useAutomaticallyAddUserToBoard from "../../utils/hooks/commonly-stored/uploaded-board/boardUser";
import { useRouter } from "next/router";
import { usePathname, useSearchParams } from "next/navigation";

export function LoadingYourBoard() {
  return <LoadingIcon text={"Loading your board"} centerHorizontally />;
}

const ReviewBoardFlow = () => {
  const { boardId, shipmentId } = useContext(
    BoardIdContext,
  ) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const customAlert = useCustomAlert();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const flashPCBSettings = useFlashPCBSettings();
  const view = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.view || null,
  );
  const errored = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.errored || false,
  );
  const notFound = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.notFound || false,
  );
  const boardIsLoaded = useAppSelector(
    (state) => !!state.uploadedBoard.boards[boardId]?.board,
  );
  const shipmentsAreLoaded = useAppSelector((state) => {
    const shipments = state.uploadedBoard.boards[boardId]?.shipments;
    return shipments !== undefined && Object.keys(shipments).length > 0;
  });
  const shipments = useAppSelector((state) => {
    return Object.values(state.uploadedBoard.boards[boardId]?.shipments || []);
  });

  useAutomaticallyAddUserToBoard();
  useLoadBoardForReviewBoardFlow();

  const resolvePageFailure = useCallback(() => {
    dispatch(pageFailureResolved({ boardId }));
  }, [dispatch, boardId]);

  useEffect(() => {
    if (errored) {
      customAlert(
        "Sorry, an error occurred while updating your board. We have logged the issue and will investigate.",
        resolvePageFailure,
      );
    }
  }, [customAlert, errored, resolvePageFailure]);

  useEffect(() => {
    if (shipmentId !== null) {
      const matchingShipment = shipments.find((shipment) => {
        return shipment.old_shipment_id == shipmentId;
      });
      if (matchingShipment !== undefined) {
        const params = new URLSearchParams(searchParams.toString());
        params.set("shipmentId", matchingShipment.id);
        router.replace(`${pathname}?${params}`);
      }
    }
  }, [pathname, router, searchParams, shipmentId, shipments]);

  if (notFound) {
    return <FourOhFour />;
  }

  if (flashPCBSettings === null || !boardIsLoaded || !shipmentsAreLoaded) {
    return <LoadingYourBoard />;
  }

  const renderElement = () => {
    if (view == BoardView.BOARD) {
      return <ReviewBoard />;
    } else if (view == BoardView.BOM) {
      return <BillOfMaterials />;
    } else if (view == BoardView.QUOTE) {
      return <Quote />;
    } else if (view == BoardView.MANUFACTURING) {
      return <ManufacturersPanel />;
    }
    return <ReviewBoard />;
  };

  return (
    <styledElements.Container>
      {renderElement()}
      <ProjectDetails />
    </styledElements.Container>
  );
};

export default ReviewBoardFlow;
